import React, { useState, useEffect } from "react";
import { seoApi } from "@/lib/seoApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Pause, Play, XCircle, RefreshCw, Repeat, Upload,
  GitBranch, ArrowRight, Loader,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const STATE_STYLE = {
  none:              { label: "Active",           color: "bg-neutral-100 text-neutral-700" },
  pause_requested:   { label: "Pausing…",         color: "bg-amber-100 text-amber-800" },
  paused:            { label: "Paused",           color: "bg-amber-100 text-amber-800" },
  cancel_requested:  { label: "Cancelling…",      color: "bg-red-100 text-red-800" },
  cancelled:         { label: "Cancelled",        color: "bg-red-100 text-red-800" },
};

function ControlBtn({ icon: Icon, label, onClick, disabled, tone = "outline", testId }) {
  const variant = tone === "danger" ? "destructive"
                    : tone === "primary" ? "default"
                    : "outline";
  return (
    <Button size="sm" variant={variant} onClick={onClick} disabled={disabled}
              data-testid={testId} className="gap-1.5">
      <Icon size={14} /> {label}
    </Button>
  );
}

export default function WorkflowControls({ run, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [role, setRole] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    seoApi.get("/acf/security/whoami")
      .then((r) => setRole(r.data?.role || "")).catch(() => setRole(""));
  }, []);

  useEffect(() => {
    if (!run?.id) return;
    seoApi.get(`/seo/workflows/runs/${run.id}/lineage`)
       .then((r) => setLineage(r.data)).catch(() => setLineage(null));
  }, [run?.id, run?.status, run?.published_id]);

  async function callAction(action, opts = {}) {
    const { path, body, method = "POST", label } = opts;
    setBusy(action);
    try {
      const r = await seoApi.request({
        url: `/seo/workflows/runs/${run.id}/${path}`,
        method,
        data: body,
      });
      const d = r.data;
      if (d.no_action) toast.info(`${label}: ${d.message}`);
      else             toast.success(`${label} — ${d.message || "ok"}`);
      if (d.child_run_id) {
        toast.success(`New run started: ${d.child_run_id.slice(0, 8)}`);
        nav(`/seo-studio/runs/${d.child_run_id}`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || `${label} failed`);
    } finally {
      setBusy(null);
    }
  }

  const state = run?.control_state || "none";
  const status = run?.status || "unknown";
  const isRunning = status === "running";
  const isPaused  = state === "paused" || state === "pause_requested";
  const isTerminal = ["success", "failed", "cancelled"].includes(status);
  const isPublished = !!run?.published_id;
  const stateInfo = STATE_STYLE[state] || STATE_STYLE.none;

  return (
    <Card data-testid="workflow-controls">
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <GitBranch size={16} /> Workflow Control
        </CardTitle>
        <div className="flex items-center gap-2">
          <Badge className={stateInfo.color}>{stateInfo.label}</Badge>
          <Badge variant="outline">status: {status}</Badge>
          {run?.publish_state === "needs_review" && (
            <Badge className="bg-orange-100 text-orange-800" data-testid="badge-needs-review">
              needs review
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary controls */}
        <div className="flex flex-wrap gap-2" data-testid="control-buttons">
          <ControlBtn icon={Pause}   label="Pause"
                        onClick={() => callAction("pause", { path: "pause", label: "Pause" })}
                        disabled={busy || !isRunning || isPaused}
                        testId="btn-pause" />
          <ControlBtn icon={Play}    label="Resume"
                        onClick={() => callAction("resume", { path: "resume", label: "Resume" })}
                        disabled={busy || !isPaused}
                        testId="btn-resume" />
          <ControlBtn icon={XCircle} label="Cancel (soft)"
                        onClick={() => callAction("cancel-soft", { path: "cancel?mode=soft", label: "Cancel" })}
                        disabled={busy || isTerminal}
                        testId="btn-cancel-soft" />
          <ControlBtn icon={XCircle} label="Cancel (hard)"  tone="danger"
                        onClick={() => window.confirm("Hard-cancel this run? It marks the run terminal immediately.") &&
                                        callAction("cancel-hard", { path: "cancel?mode=hard", label: "Hard cancel" })}
                        disabled={busy || isTerminal}
                        testId="btn-cancel-hard" />
        </div>

        <div className="border-t border-neutral-100 pt-3">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
            Retry & Republish
          </div>
          <div className="flex flex-wrap gap-2">
            <ControlBtn icon={RefreshCw} label="Retry (full)" tone="primary"
                          onClick={() => callAction("retry", { path: "retry", body: {}, label: "Retry" })}
                          disabled={busy || !run?.brief_id}
                          testId="btn-retry-full" />
            <ControlBtn icon={Repeat} label="Retry Text.ru"
                          onClick={() => callAction("retry-textru", {
                            path: "retry-from-stage",
                            body: { stage_type: "textru" },
                            label: "Retry Text.ru",
                          })}
                          disabled={busy || !run?.content}
                          testId="btn-retry-textru" />
            <ControlBtn icon={Repeat} label="Retry Images"
                          onClick={() => callAction("retry-image", {
                            path: "retry-from-stage",
                            body: { stage_type: "image" },
                            label: "Retry Images",
                          })}
                          disabled={busy || !run?.content}
                          testId="btn-retry-image" />
            <ControlBtn icon={Upload} label="Republish"
                          onClick={() => window.confirm("Republish will UPDATE the existing CMS document (slug/URL preserved). Continue?") &&
                                          callAction("republish", { path: "republish", label: "Republish" })}
                          disabled={busy || !isPublished}
                          testId="btn-republish" />
          </div>
        </div>

        {/* Uniqueness report (dual-layer gate) */}
        {run?.textru?.uniqueness_pct != null && (
          <div className="border-t border-neutral-100 pt-3" data-testid="uniqueness-report">
            <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
              Uniqueness (Text.ru)
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-neutral-700">
              <span data-testid="uniq-overall">
                Overall: <b>{run.textru.uniqueness_pct}%</b>
              </span>
              {run.textru.prose_uniqueness_pct != null && (
                <span data-testid="uniq-prose">
                  Authorial prose: <b>{run.textru.prose_uniqueness_pct}%</b>
                </span>
              )}
              {run.textru.matched_authorial != null && (
                <span data-testid="uniq-breakdown" className="text-neutral-500">
                  matches: {run.textru.matched_authorial} authorial words
                  {" / "}{run.textru.matched_immutable} immutable
                  {run.textru.immutable_share_pct != null &&
                    ` (${run.textru.immutable_share_pct}% of overlap is fixed data)`}
                </span>
              )}
            </div>
            {run?.publish_state === "needs_review" && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-orange-700" data-testid="needs-review-msg">
                  Below threshold after auto-rewrite — publish blocked.
                  {" "}<Link to="/seo-studio/review" className="underline">Review Queue</Link> has fix / approve actions.
                </span>
                {role === "super-admin" && (
                  <ControlBtn icon={Upload} label="Publish anyway (admin)" tone="danger"
                                onClick={() => window.confirm("Publish DESPITE low prose uniqueness? The gate will be skipped.") &&
                                                callAction("publish-skip-gate", {
                                                  path: "publish",
                                                  body: { skip_uniqueness_gate: true },
                                                  label: "Publish (gate skipped)",
                                                })}
                                disabled={busy}
                                testId="btn-publish-skip-gate" />
                )}
              </div>
            )}
          </div>
        )}

        {/* Retry Chain */}
        {lineage && lineage.count > 1 && (
          <div className="border-t border-neutral-100 pt-3" data-testid="retry-chain">
            <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
              Retry Chain ({lineage.count} runs)
            </div>
            <div className="flex flex-wrap items-center gap-1 text-xs">
              {lineage.chain.map((r, i) => {
                const isThis = r.id === run.id;
                return (
                  <React.Fragment key={r.id}>
                    {i > 0 && <ArrowRight size={12} className="text-neutral-400" />}
                    <Link to={`/seo-studio/runs/${r.id}`}
                            className={`px-2 py-1 rounded font-mono border transition ${
                              isThis ? "bg-neutral-900 text-white border-neutral-900"
                                     : "bg-neutral-50 text-neutral-700 border-neutral-200 hover:bg-neutral-100"
                            }`}
                            title={`${r.status}${r.published_id ? " · published" : ""}`}>
                      {r.id.slice(0, 8)}
                      <span className="ml-1 text-[10px] opacity-70">{r.status}</span>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {busy && (
          <div className="text-xs text-neutral-500 flex items-center gap-2">
            <Loader className="animate-spin" size={12} /> {busy}…
          </div>
        )}
      </CardContent>
    </Card>
  );
}
